import { createFileRoute } from "@tanstack/react-router";
import { Mail, Sparkles, Send } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroAsset from "@/assets/platform/welcome-email/hero.webp.asset.json";
import designAsset from "@/assets/platform/welcome-email/design.webp.asset.json";
import trustAsset from "@/assets/platform/welcome-email/trust.webp.asset.json";

const heroImg = heroAsset.url;
const designImg = designAsset.url;
const trustImg = trustAsset.url;

const heroAlt =
  "Welcome email Banana cá nhân hoá mở trong Gmail trên iPhone ngay sau khi khách trao đổi thông tin, kèm nút Xem hồ sơ của tôi";
const designAlt =
  "Trình dựng automation welcome email Banana trên iPhone với trigger 'Khi người dùng trao đổi thông tin', kéo-thả biến First Name và trình soạn nội dung rich-text";
const trustAlt =
  "Hai iPhone hiển thị thông báo email liên hệ mới của Banana và welcome email tiếng Việt được gửi từ domain riêng vào inbox thay vì spam";

export const Route = createFileRoute("/vi/platform/networking/welcome-email")({
  head: () => ({
    meta: [
      { title: "Welcome Email — banana.cards" },
      { name: "description", content: "Tự gửi welcome email tuỳ chỉnh ngay khi ai kết nối — top of mind từ phút đầu." },
      { property: "og:title", content: "Welcome Email — banana.cards" },
      { property: "og:description", content: "Email chào tự động ngay khi kết nối." },
      { property: "og:image", content: heroImg },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/networking/welcome-email", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Welcome Email"
      title="Ấn tượng đầu"
      italic="tự follow-up mình."
      description="Ngay khi ai trao đổi liên hệ, Banana gửi welcome email tùy chỉnh — thiết kế đẹp, cá nhân hoá, từ địa chỉ của bạn."
      heroImage={heroImg}
      heroImageAlt={heroAlt}
      metrics={[{ v: "Tức thì", l: "delivery" }, { v: "Custom", l: "branding" }, { v: "100%", l: "deliverable" }]}
      bullets={[
        { icon: Sparkles, title: "Message tùy chỉnh", desc: "Viết một lần — Banana cá nhân hoá với tên người nhận mỗi lần gửi." },
        { icon: Mail, title: "Từ domain của bạn", desc: "Gửi từ địa chỉ của bạn với SPF/DKIM đúng để vào inbox, không spam." },
        { icon: Send, title: "Fire tự động", desc: "Trigger ngay khi form trao đổi submit — không cần follow-up thủ công." }
      ]}
      deepDives={[
        { eyebrow: "Thiết kế", title: "Như bạn tự viết,", italic: "vì bạn viết.", description: "Soạn welcome branded với ảnh, link và CTA. Banana giữ ngắn và human — không mùi template.", image: designImg, imageAlt: designAlt, reverse: false, points: ["Rich-text composer", "Merge tag cá nhân hoá", "Link & CTA nhúng", "Hỗ trợ đa ngôn ngữ"] },
        { eyebrow: "Tin cậy", title: "Deliverability", italic: "cấp inbox.", description: "Gửi từ domain riêng với authentication. Banana xử lý plumbing để follow-up đến nơi.", image: trustImg, imageAlt: trustAlt, reverse: true, points: ["SPF, DKIM và DMARC", "Xử lý bounce & complaint", "Sender identity mỗi thẻ", "Send log mỗi contact"] }
      ]}
      howItWorks={[
        { title: "Viết message", desc: "Soạn 1 lần — dùng merge tag cho tên." },
        { title: "Kết nối domain", desc: "Verify SPF & DKIM trong vài click." },
        { title: "Ai đó kết nối", desc: "Submit form trigger gửi." },
        { title: "Họ nhận tức thì", desc: "Đẹp, cá nhân, trong inbox." }
      ]}
      capabilities={["Rich-text composer", "Merge tag & cá nhân hoá", "Gửi từ domain riêng", "SPF / DKIM / DMARC", "Welcome message mỗi thẻ", "Đa ngôn ngữ", "Send log", "Trigger từ form trao đổi"]}
      faq={[
        { q: "Welcome Email là gì và gửi khi nào?", a: "Email tự động Banana gửi cho ai trao đổi liên hệ (qua Lead Exchange, tap thẻ, Wallet save). Fire trong vài giây — khi bạn còn trong cuộc trò chuyện." },
        { q: "Có gửi từ email riêng của tôi không?", a: "Có — verify domain trong Banana, welcome đi từ địa chỉ bạn, vào primary inbox, và reply về thẳng bạn." },
        { q: "Có cá nhân hoá theo người nhận không?", a: "Có — dùng biến {{first_name}}, {{company}}, {{event}}, {{your_name}}. AI Brain có thể draft phiên bản đầu trong tone của bạn." },
        { q: "Welcome khác nhau cho thẻ/sự kiện khác nhau được không?", a: "Có — mỗi thẻ mang welcome email riêng." }
      ]}
      outro={{ title: "Contact đầu tiên,", italic: "reply đầu tiên.", description: "Welcome mọi kết nối mới tự động — và bắt đầu mối quan hệ đúng cách." }}
    />
  );
}